import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../../api';

const masterConfig = {
  income: {
    title: 'Receipt Types',
    endpoint: '/api/income-types',
    idKey: 'income_type_id',
    nameKey: 'income_type_name',
    placeholder: 'Receipt type name',
    supportsLoanRequirement: true,
    supportsProfitImpact: true,
    defaultAffectsProfit: false,
  },
  expense: {
    title: 'Payment Types',
    endpoint: '/api/expense-types',
    idKey: 'expense_type_id',
    nameKey: 'expense_type_name',
    placeholder: 'Payment type name',
    supportsProfitImpact: true,
    defaultAffectsProfit: true,
  },
  goldBank: {
    title: 'Gold Pledge Banks',
    endpoint: '/api/gold-repledge-banks',
    listEndpoint: '/api/gold-repledge-banks?include_inactive=true',
    idKey: 'bank_id',
    nameKey: 'bank_name',
    placeholder: 'Bank name',
    secondaryKey: 'branch_name',
    secondaryLabel: 'Branch',
    secondaryPlaceholder: 'Branch name',
  },
};

const Settings = () => {
  const [activeMaster, setActiveMaster] = useState('income');

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: 'navy', margin: '0 0 18px' }}>Settings</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {Object.entries(masterConfig).map(([key, config]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveMaster(key)}
            style={{
              padding: '8px 16px',
              border: `1px solid ${activeMaster === key ? '#1976d2' : '#cfd6e2'}`,
              borderRadius: 4,
              background: activeMaster === key ? '#1976d2' : '#fff',
              color: activeMaster === key ? '#fff' : '#344054',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {config.title}
          </button>
        ))}
      </div>
      <MasterPanel config={masterConfig[activeMaster]} />
    </div>
  );
};

const MasterPanel = ({ config }) => {
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [name, setName] = useState('');
  const [secondaryValue, setSecondaryValue] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [requiresLoan, setRequiresLoan] = useState(false);
  const [affectsProfit, setAffectsProfit] = useState(config.defaultAffectsProfit !== false);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const filteredRows = useMemo(() => {
    const text = filterText.trim().toLowerCase();
    if (!text) return rows;
    return rows.filter(row => [row[config.nameKey], config.secondaryKey ? row[config.secondaryKey] : '']
      .some(value => String(value || '').toLowerCase().includes(text)));
  }, [config.nameKey, config.secondaryKey, filterText, rows]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${config.listEndpoint || config.endpoint}`);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data.error || `Unable to load ${config.title}.`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || `Unable to load ${config.title}.`);
    } finally {
      setLoading(false);
    }
  }, [config.endpoint, config.listEndpoint, config.title]);

  useEffect(() => {
    setSelectedRow(null);
    setName('');
    setSecondaryValue('');
    setIsActive(true);
    setRequiresLoan(false);
    setAffectsProfit(config.defaultAffectsProfit !== false);
    setFilterText('');
    loadRows();
  }, [config.defaultAffectsProfit, loadRows]);

  const selectRow = row => {
    setSelectedRow(row);
    setName(row[config.nameKey] || '');
    setSecondaryValue(config.secondaryKey ? row[config.secondaryKey] || '' : '');
    setIsActive(row.is_active !== false);
    setRequiresLoan(row.requires_loan === true);
    setAffectsProfit(row.affects_profit === true);
    setError('');
    setSuccess('');
  };

  const resetForm = () => {
    setSelectedRow(null);
    setName('');
    setSecondaryValue('');
    setIsActive(true);
    setRequiresLoan(false);
    setAffectsProfit(config.defaultAffectsProfit !== false);
    setError('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(`${config.title.replace(/s$/, '')} name is required.`);
      return;
    }
    const trimmedSecondaryValue = secondaryValue.trim();
    if (config.secondaryKey && !trimmedSecondaryValue) {
      setError(`${config.secondaryLabel} is required.`);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const isEdit = Boolean(selectedRow);
      const url = isEdit
        ? `${API_BASE_URL}${config.endpoint}/${selectedRow[config.idKey]}`
        : `${API_BASE_URL}${config.endpoint}`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          [config.nameKey]: trimmedName,
          ...(config.secondaryKey ? { [config.secondaryKey]: trimmedSecondaryValue } : {}),
          is_active: isActive,
          ...(config.supportsLoanRequirement ? { requires_loan: requiresLoan } : {}),
          ...(config.supportsProfitImpact ? { affects_profit: affectsProfit } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Unable to save ${config.title}.`);

      setSuccess(`${config.title.replace(/s$/, '')} saved.`);
      resetForm();
      await loadRows();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      setError(err.message || `Unable to save ${config.title}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 13, color: '#444', minWidth: 280 }}>
          {config.title.replace(/s$/, '')}
          <input
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder={config.placeholder}
            style={{ padding: 8, fontSize: 13, border: '1px solid #cfd8e3', borderRadius: 4 }}
          />
        </label>
        {config.secondaryKey && (
          <label style={{ display: 'grid', gap: 4, fontSize: 13, color: '#444', minWidth: 240 }}>
            {config.secondaryLabel}
            <input
              value={secondaryValue}
              onChange={event => setSecondaryValue(event.target.value)}
              placeholder={config.secondaryPlaceholder}
              style={{ padding: 8, fontSize: 13, border: '1px solid #cfd8e3', borderRadius: 4 }}
            />
          </label>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, paddingBottom: 8 }}>
          <input type="checkbox" checked={isActive} onChange={event => setIsActive(event.target.checked)} />
          Active
        </label>
        {config.supportsLoanRequirement && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, paddingBottom: 8 }}>
            <input type="checkbox" checked={requiresLoan} onChange={event => setRequiresLoan(event.target.checked)} />
            Loan required
          </label>
        )}
        {config.supportsProfitImpact && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, paddingBottom: 8 }}>
            <input type="checkbox" checked={affectsProfit} onChange={event => setAffectsProfit(event.target.checked)} />
            Affects profit
          </label>
        )}
        <button type="submit" disabled={saving} style={{ padding: '8px 16px', border: 'none', borderRadius: 4, background: saving ? '#98a2b3' : '#1976d2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
          {selectedRow ? 'Update' : 'Add'}
        </button>
        <button type="button" onClick={resetForm} style={{ padding: '8px 16px', border: '1px solid #cfd6e2', borderRadius: 4, background: '#fff', color: '#344054', cursor: 'pointer' }}>
          New
        </button>
      </form>

      <div className="mobile-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 14, color: '#555' }}>{filteredRows.length} records</span>
        <input value={filterText} onChange={event => setFilterText(event.target.value)} placeholder="Filter master" style={{ padding: 7, minWidth: 220, fontSize: 13 }} />
        <button type="button" onClick={loadRows} style={{ padding: '7px 14px', fontSize: 13, background: '#fff', border: '1px solid #cfd6e2', borderRadius: 4, cursor: 'pointer' }}>Refresh</button>
      </div>

      {error && <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ color: '#067647', marginBottom: 12, fontWeight: 600 }}>{success}</div>}

      <div className="desktop-table-wrap">
        <table className="fixed-header-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', boxShadow: '0 1px 4px #eee' }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>ID</th>
              <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>Name</th>
              {config.secondaryKey && <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>{config.secondaryLabel}</th>}
              {config.supportsLoanRequirement && <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>Loan</th>}
              {config.supportsProfitImpact && <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>Profit</th>}
              <th style={{ padding: '7px 6px', borderBottom: '1px solid #ccc', textAlign: 'left', background: '#fafbfc' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={2 + (config.secondaryKey ? 1 : 0) + (config.supportsLoanRequirement ? 1 : 0) + (config.supportsProfitImpact ? 1 : 0) + 1} style={{ padding: 12 }}>Loading...</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={2 + (config.secondaryKey ? 1 : 0) + (config.supportsLoanRequirement ? 1 : 0) + (config.supportsProfitImpact ? 1 : 0) + 1} style={{ padding: 12 }}>No records found.</td></tr>
            ) : filteredRows.map(row => (
              <tr
                key={row[config.idKey]}
                className={selectedRow?.[config.idKey] === row[config.idKey] ? 'selected-record-row' : undefined}
                onClick={() => selectRow(row)}
                style={{ cursor: 'pointer' }}
              >
                <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row[config.idKey]}</td>
                <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row[config.nameKey]}</td>
                {config.secondaryKey && <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row[config.secondaryKey]}</td>}
                {config.supportsLoanRequirement && <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row.requires_loan === true ? 'Required' : 'Optional'}</td>}
                {config.supportsProfitImpact && <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row.affects_profit === true ? 'Yes' : 'No'}</td>}
                <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{row.is_active === false ? 'Inactive' : 'Active'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mobile-card-list">
        {loading ? (
          <div className="mobile-record-card">Loading...</div>
        ) : filteredRows.length === 0 ? (
          <div className="mobile-record-card">No records found.</div>
        ) : filteredRows.map(row => (
          <div
            key={row[config.idKey]}
            className={`mobile-record-card ${selectedRow?.[config.idKey] === row[config.idKey] ? 'selected' : ''}`}
            onClick={() => selectRow(row)}
          >
            <div className="mobile-card-title">
              <div>
                {row[config.nameKey]}
                <div className="mobile-card-subtitle">ID {row[config.idKey]}</div>
              </div>
              <span className="mobile-badge">{row.is_active === false ? 'Inactive' : 'Active'}</span>
            </div>
            {config.secondaryKey && (
              <div className="mobile-card-grid">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">{config.secondaryLabel}</span>
                  <span className="mobile-card-value">{row[config.secondaryKey]}</span>
                </div>
              </div>
            )}
            {config.supportsLoanRequirement && (
              <div className="mobile-card-grid">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">Loan</span>
                  <span className="mobile-card-value">{row.requires_loan === true ? 'Required' : 'Optional'}</span>
                </div>
              </div>
            )}
            {config.supportsProfitImpact && (
              <div className="mobile-card-grid">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">Profit</span>
                  <span className="mobile-card-value">{row.affects_profit === true ? 'Yes' : 'No'}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default Settings;
